// Payment panel for the B2B wholesale screen.
// Supports cash / bank-transfer payment methods.
// Partial payment (tendered < total) requires a customer and a reason.
// After commit shows success state with print + new-order actions.

import { useState, useCallback } from "react";
import { Banknote, Building2, CheckCircle2, Printer, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiPost } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import type { Order } from "@/features/pos/use-pos-cart";

interface Props {
  order: Order | null;
  workstationId: string | null;
  loading: boolean;
  onCommit: (params: {
    paymentMethod: string;
    tenderedAmount: number;
    reason?: string;
  }) => void;
  onNewOrder: () => void;
}

type Method = "cash" | "transfer";

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0";
  return new Intl.NumberFormat("vi-VN").format(n);
}

function parseVND(s: string) {
  return parseFloat(s.replace(/[^\d.]/g, "")) || 0;
}

export function WholesalePaymentPanel({ order, workstationId, loading, onCommit, onNewOrder }: Props) {
  const [method, setMethod] = useState<Method>("transfer");
  const [tenderedStr, setTenderedStr] = useState("");
  const [reason, setReason] = useState("");
  const [printing, setPrinting] = useState(false);

  const isPaid = order?.status === "paid";
  const total = parseVND(order?.total ?? "0");
  const tendered = tenderedStr.trim() ? parseVND(tenderedStr) : total;
  const isPartial = tendered < total && tendered > 0;
  const hasCustomer = !!order?.customerId;
  const debtAmount = isPartial ? total - tendered : 0;

  const canCommit =
    !isPaid &&
    !!order?.items?.length &&
    tendered > 0 &&
    (!isPartial || (hasCustomer && reason.trim().length >= 5));

  const handleCommit = useCallback(() => {
    if (!canCommit) return;
    onCommit({
      paymentMethod: method === "transfer" ? "bank_transfer" : "cash",
      tenderedAmount: tendered,
      reason: isPartial ? reason.trim() : undefined,
    });
  }, [canCommit, method, tendered, isPartial, reason, onCommit]);

  const handlePrint = useCallback(async () => {
    if (!order?.id) return;
    setPrinting(true);
    try {
      const url = `/v1/orders/${order.id}/print${workstationId ? `?workstation_id=${workstationId}` : ""}`;
      await apiPost(url);
      toast.success("Đã gửi lệnh in phiếu");
    } catch (err) {
      toast.error(translateApiError(err));
    } finally {
      setPrinting(false);
    }
    // TODO: dedicated A4/wholesale print template — deferred to post-MVP
  }, [order?.id, workstationId]);

  // Success state — after commit
  if (isPaid) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center px-4">
        <CheckCircle2 size={48} className="text-green-500" />
        <div>
          <p className="text-lg font-bold text-green-600">Đơn sỉ hoàn tất</p>
          <p className="text-sm text-muted-foreground">{order?.code}</p>
          {parseFloat(order?.debtAmount ?? "0") > 0 && (
            <p className="mt-1 text-sm font-medium text-amber-600">
              Ghi nợ: {fmtVND(order?.debtAmount ?? "0")} ₫
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={printing}
            className="gap-1.5"
          >
            <Printer size={14} />
            {printing ? "Đang in..." : "In phiếu"}
          </Button>
          <Button size="sm" onClick={onNewOrder} className="gap-1.5">
            <Plus size={14} />
            Đơn mới
          </Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground px-4">
        Chưa có đơn. Thêm sản phẩm để bắt đầu.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Order totals */}
      <div className="rounded-lg border bg-muted/30 p-3 space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Tạm tính</span>
          <span className="font-mono">{fmtVND(order.subtotal)} ₫</span>
        </div>
        {parseFloat(order.discountAmount) > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Giảm giá</span>
            <span className="font-mono">−{fmtVND(order.discountAmount)} ₫</span>
          </div>
        )}
        <div className="flex justify-between font-semibold border-t pt-1 mt-1">
          <span>Tổng cộng</span>
          <span className="font-mono text-primary">{fmtVND(order.total)} ₫</span>
        </div>
      </div>

      {/* Payment method */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Hình thức TT</Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setMethod("cash")}
            className={[
              "flex items-center justify-center gap-2 rounded-md border p-2.5 text-sm transition-colors",
              method === "cash"
                ? "border-primary bg-primary/10 text-primary font-medium"
                : "hover:bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <Banknote size={15} />
            Tiền mặt
          </button>
          <button
            type="button"
            onClick={() => setMethod("transfer")}
            className={[
              "flex items-center justify-center gap-2 rounded-md border p-2.5 text-sm transition-colors",
              method === "transfer"
                ? "border-primary bg-primary/10 text-primary font-medium"
                : "hover:bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <Building2 size={15} />
            Chuyển khoản
          </button>
        </div>
      </div>

      {/* Tendered amount */}
      <div className="space-y-1">
        <Label htmlFor="tendered-input" className="text-xs text-muted-foreground uppercase tracking-wide">
          Khách trả (để trống = thanh toán đủ)
        </Label>
        <Input
          id="tendered-input"
          type="number"
          min={0}
          placeholder={fmtVND(order.total)}
          value={tenderedStr}
          onChange={(e) => setTenderedStr(e.target.value)}
          className="font-mono text-right"
        />
        {isPartial && (
          <p className="text-xs text-amber-600">
            Ghi nợ: <span className="font-semibold font-mono">{fmtVND(String(debtAmount))} ₫</span>
          </p>
        )}
      </div>

      {/* Partial payment: reason required */}
      {isPartial && (
        <div className="space-y-1">
          <Label htmlFor="reason-input" className="text-xs text-muted-foreground uppercase tracking-wide">
            Lý do ghi nợ <span className="text-destructive">*</span>
          </Label>
          {!hasCustomer && (
            <p className="text-xs text-destructive">Thanh toán một phần cần chọn khách hàng.</p>
          )}
          <Input
            id="reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ví dụ: CK 50% còn lại sẽ trả sau 7 ngày..."
            className="text-sm"
          />
          {reason.trim().length > 0 && reason.trim().length < 5 && (
            <p className="text-xs text-destructive">Lý do tối thiểu 5 ký tự.</p>
          )}
        </div>
      )}

      {/* Commit */}
      <Button
        className="w-full"
        size="lg"
        disabled={!canCommit || loading}
        onClick={handleCommit}
      >
        {loading ? "Đang xử lý..." : "Xác nhận đơn sỉ"}
      </Button>

      {isPartial && !canCommit && !loading && (
        <p className="text-center text-xs text-muted-foreground">
          Ghi nợ yêu cầu: chọn khách hàng + lý do (≥ 5 ký tự).
        </p>
      )}
    </div>
  );
}
